import { CREATIVE_TEMPLATE_MAX_NODES } from "../../../utils/constants";

export const ORIGIN_COLORS = {
  generated: "#7C3AED",
  data: "#2563EB",
  computed: "#16A34A",
  asset: "#52525B",
  fixed: "#52525B",
};

export const getOriginColor = ({ sourceKind, type }) => {
  if (type === "group" || type === "shape") return ORIGIN_COLORS.asset;
  return ORIGIN_COLORS[sourceKind] || ORIGIN_COLORS.asset;
};

export const slugifyName = ({ name, fallback = "layer" }) => {
  const slug = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || fallback;
};

export const uniqueName = ({ name, existingNames = [] }) => {
  let next = name;
  let suffix = 2;
  while (existingNames.includes(next)) {
    next = `${name}_${suffix}`;
    suffix += 1;
  }
  return next;
};

export const listNodeNames = ({ nodes = {} }) => {
  const names = [];
  Object.values(nodes).forEach((node) => {
    names.push(node.name);
    names.push(...listNodeNames({ nodes: node.children || {} }));
  });
  return names;
};

export const countNodes = ({ nodes = {} }) =>
  Object.values(nodes).reduce((total, node) => {
    return total + 1 + countNodes({ nodes: node.children || {} });
  }, 0);

export const canProduceCreativeTemplate = ({ nodeCount, nodes } = {}) => {
  if (nodeCount != null) return Number(nodeCount) > 0;
  return countNodes({ nodes: nodes || {} }) > 0;
};

export const normalizeChildOrder = ({ childOrder, children = {} }) => {
  if (Array.isArray(childOrder) && childOrder.length) return childOrder;
  if (childOrder && typeof childOrder === "object") {
    return Object.keys(childOrder)
      .sort((left, right) => Number(left) - Number(right))
      .map((key) => childOrder[key])
      .filter(Boolean);
  }
  return Object.keys(children);
};

export const findNodePath = ({ nodes = {}, nodeID, parentID = null }) => {
  const node = nodes[nodeID];
  if (node) {
    return { node, parentID, siblings: nodes };
  }
  const entries = Object.values(nodes);
  for (let index = 0; index < entries.length; index += 1) {
    const current = entries[index];
    const nested = findNodePath({
      nodes: current.children || {},
      nodeID,
      parentID: current.id,
    });
    if (nested) return nested;
  }
  return null;
};

export const resolveInsertParentID = ({ nodes = {}, selectedID }) => {
  if (!selectedID) return null;
  const found = findNodePath({ nodes, nodeID: selectedID });
  if (!found) return null;
  if (found.node?.type === "group") return found.node.id;
  return found.parentID || null;
};

const omitUndefinedDeep = (value) => {
  if (Array.isArray(value)) return value.map(omitUndefinedDeep);
  if (!value || typeof value !== "object") return value;
  const next = {};
  Object.entries(value).forEach(([key, item]) => {
    if (item === undefined) return;
    next[key] = omitUndefinedDeep(item);
  });
  return next;
};

export const patchNodeById = ({ nodes = {}, nodeID, patch }) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (id === nodeID) {
      next[id] = omitUndefinedDeep({ ...node, ...patch });
      return;
    }
    next[id] = {
      ...node,
      children: node.children
        ? patchNodeById({ nodes: node.children, nodeID, patch })
        : node.children,
    };
  });
  return next;
};

export const deleteNodeById = ({ nodes = {}, nodeLayouts = {}, nodeID }) => {
  if (nodes[nodeID]) {
    const nextNodes = { ...nodes };
    delete nextNodes[nodeID];
    const nextLayouts = { ...nodeLayouts };
    delete nextLayouts[nodeID];
    return { nodes: nextNodes, nodeLayouts: nextLayouts };
  }
  const nextNodes = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (!node.children?.[nodeID]) {
      nextNodes[id] = {
        ...node,
        children: node.children
          ? deleteNodeById({
              nodes: node.children,
              nodeLayouts: {},
              nodeID,
            }).nodes
          : node.children,
      };
      return;
    }
    const children = { ...node.children };
    delete children[nodeID];
    nextNodes[id] = {
      ...node,
      children,
      childOrder: normalizeChildOrder({
        childOrder: node.childOrder,
        children,
      }).filter((childID) => childID !== nodeID),
    };
  });
  return { nodes: nextNodes, nodeLayouts };
};

const defaultSourceForType = ({ type, name, id }) => {
  if (type === "text") {
    return { kind: "data", variable: slugifyName({ name, fallback: id }) };
  }
  if (type === "image") {
    return { kind: "asset", assetRef: null };
  }
  return undefined;
};

export const createDefaultNode = ({
  id,
  name,
  existingNames = [],
  type = "text",
}) => {
  const unique = uniqueName({ name, existingNames });
  const node = {
    id,
    name: unique,
    type,
  };
  if (type === "group") {
    node.children = {};
    node.childOrder = [];
    node.componentFrame = { width: 200, height: 200 };
    node.clipContent = false;
    return node;
  }
  if (type === "shape") {
    node.shape = {
      kind: "roundedRectangle",
      radius: 8,
      fill: "#46248C",
    };
    return node;
  }
  node.source = defaultSourceForType({ type, name: unique, id });
  if (type === "image") node.fit = "contain";
  if (type === "text") {
    node.style = {
      fontSize: 24,
      color: "#FFFFFF",
      align: "left",
      orientation: "horizontal",
    };
    node.overflow = { strategy: "shrink" };
  }
  return node;
};

export const applyNodeType = ({ node, type, layout }) => {
  const next = createDefaultNode({
    id: node.id,
    name: node.name,
    existingNames: [],
    type,
  });
  next.name = node.name;
  if (type === "group") {
    next.componentFrame = {
      width: layout?.width || node.componentFrame?.width || 200,
      height: layout?.height || node.componentFrame?.height || 200,
    };
    next.children = node.type === "group" ? node.children || {} : {};
    next.childOrder =
      node.type === "group"
        ? normalizeChildOrder({
            childOrder: node.childOrder,
            children: next.children,
          })
        : [];
  } else {
    next.children = undefined;
    next.childOrder = undefined;
    next.componentFrame = undefined;
    next.clipContent = undefined;
  }
  if (type === "group" || type === "shape") next.source = undefined;
  if (type !== "shape") next.shape = undefined;
  if (type !== "text") {
    next.style = undefined;
    next.overflow = undefined;
  } else {
    next.overflow = { ...(node.overflow || {}), strategy: "shrink" };
  }
  if (type === "image") next.fit = "contain";
  else next.fit = undefined;
  return next;
};

export const canBeCanvasBackground = ({ node, parentID } = {}) =>
  parentID == null &&
  node?.type === "image" &&
  node?.source?.kind === "generated";

export const isCanvasBackgroundNode = ({ node, parentID } = {}) =>
  canBeCanvasBackground({ node, parentID }) && Boolean(node?.isBackground);

const stripCanvasBackground = (node) => {
  if (!node?.isBackground) return node;
  const next = { ...node };
  delete next.isBackground;
  if (next.type === "image") next.fit = "contain";
  return next;
};

export const applyCanvasBackgroundLayout = ({
  nodeLayouts = {},
  nodeID,
  width,
  height,
}) => ({
  ...nodeLayouts,
  [nodeID]: {
    ...(nodeLayouts[nodeID] || {}),
    x: 0,
    y: 0,
    width: Math.round(Number(width) || 0),
    height: Math.round(Number(height) || 0),
    zIndex: 0,
    visible: true,
  },
});

export const setRootCanvasBackground = ({
  nodes = {},
  nodeID,
  isBackground,
}) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    if (!node) return;
    if (id === nodeID) {
      if (isBackground && canBeCanvasBackground({ node, parentID: null })) {
        next[id] = { ...node, isBackground: true, fit: "cover" };
        return;
      }
      next[id] = stripCanvasBackground(node);
      return;
    }
    if (isBackground && node.isBackground) {
      next[id] = stripCanvasBackground(node);
      return;
    }
    next[id] = node;
  });
  return next;
};

export const applySourceKind = ({ node, kind }) => {
  if (node.type === "group" || node.type === "shape") {
    const next = { ...node };
    delete next.source;
    return stripCanvasBackground(next);
  }
  if (
    node.type === "text" &&
    !["fixed", "data", "computed", "generated"].includes(kind)
  ) {
    return node;
  }
  if (node.type === "image" && !["asset", "data", "generated"].includes(kind)) {
    return node;
  }
  let next = node;
  if (kind === "fixed") {
    next = { ...node, source: { kind, value: node.source?.value || "" } };
  } else if (kind === "asset") {
    next = {
      ...node,
      source: { kind, assetRef: node.source?.assetRef || null },
    };
  } else if (kind === "data") {
    next = {
      ...node,
      source: {
        kind,
        variable:
          node.source?.variable ||
          slugifyName({ name: node.name, fallback: node.id }),
      },
    };
  } else if (kind === "computed") {
    next = {
      ...node,
      source: { kind, expression: node.source?.expression || "" },
    };
  } else {
    next = {
      ...node,
      source: {
        kind: "generated",
        generator: node.type === "text" ? "text" : "image",
        promptTemplate: node.source?.promptTemplate || "",
      },
    };
  }
  if (!(next.type === "image" && next.source?.kind === "generated")) {
    return stripCanvasBackground(next);
  }
  return next;
};

const pxBox = ({ nx, ny, nw, nh, width, height }) => ({
  x: Math.round(nx * width),
  y: Math.round(ny * height),
  width: Math.round(nw * width),
  height: Math.round(nh * height),
});

export const getAbsolutePx = ({
  nodes,
  nodeLayouts,
  nodeID,
  artboardWidth,
  artboardHeight,
}) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return null;
  if (!found.parentID) {
    const layout = nodeLayouts[nodeID] || {};
    return {
      x: layout.x || 0,
      y: layout.y || 0,
      width: layout.width || 0,
      height: layout.height || 0,
      visible: layout.visible !== false,
      zIndex: layout.zIndex || 0,
    };
  }
  const parentBox = getAbsolutePx({
    nodes,
    nodeLayouts,
    nodeID: found.parentID,
    artboardWidth,
    artboardHeight,
  });
  const layout = found.node.layout || {};
  return {
    x: (parentBox?.x || 0) + (layout.x || 0),
    y: (parentBox?.y || 0) + (layout.y || 0),
    width: layout.width || 0,
    height: layout.height || 0,
    visible: layout.visible !== false && parentBox?.visible !== false,
    zIndex: layout.zIndex || 0,
  };
};

const compareRootPaintOrder = ({ left, right, nodeLayouts = {} }) => {
  const leftLayout = nodeLayouts[left] || {};
  const rightLayout = nodeLayouts[right] || {};
  const zDiff = (leftLayout.zIndex || 0) - (rightLayout.zIndex || 0);
  if (zDiff !== 0) return zDiff;
  const leftArea = (leftLayout.width || 0) * (leftLayout.height || 0);
  const rightArea = (rightLayout.width || 0) * (rightLayout.height || 0);
  return rightArea - leftArea;
};

export const getRootOrder = ({ nodes = {}, nodeLayouts = {} }) =>
  Object.keys(nodes).sort((left, right) =>
    compareRootPaintOrder({ left, right, nodeLayouts })
  );

export const flattenTreeToBoxes = ({
  nodes = {},
  nodeLayouts = {},
  artboardWidth,
  artboardHeight,
}) => {
  if (!artboardWidth || !artboardHeight) return [];
  const boxes = [];
  const walk = ({ map, parentID }) => {
    const ordered = parentID
      ? normalizeChildOrder({
          childOrder: findNodePath({ nodes, nodeID: parentID })?.node
            ?.childOrder,
          children: map,
        })
      : getRootOrder({ nodes: map, nodeLayouts });
    ordered.forEach((id) => {
      const node = map[id];
      if (!node) return;
      const abs = getAbsolutePx({
        nodes,
        nodeLayouts,
        nodeID: id,
        artboardWidth,
        artboardHeight,
      });
      if (!abs || abs.visible === false) return;
      boxes.push({
        id: node.id,
        name: node.name,
        type: node.type,
        sourceKind: node.source?.kind,
        assetRef: node.source?.assetRef || "",
        fit: node.fit || "contain",
        parentID,
        isBackground: isCanvasBackgroundNode({ node, parentID }),
        nx: abs.x / artboardWidth,
        ny: abs.y / artboardHeight,
        nw: abs.width / artboardWidth,
        nh: abs.height / artboardHeight,
        zIndex: abs.zIndex || 0,
        paintIndex: boxes.length,
      });
      walk({ map: node.children || {}, parentID: id });
    });
  };
  walk({ map: nodes, parentID: null });
  return boxes;
};

export const applyOverlayBox = ({
  nodes,
  nodeLayouts,
  nodeID,
  nx,
  ny,
  nw,
  nh,
  artboardWidth,
  artboardHeight,
}) => {
  const abs = pxBox({
    nx,
    ny,
    nw,
    nh,
    width: artboardWidth,
    height: artboardHeight,
  });
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  if (isCanvasBackgroundNode({ node: found.node, parentID: found.parentID })) {
    return {
      nodes,
      nodeLayouts: applyCanvasBackgroundLayout({
        nodeLayouts,
        nodeID,
        width: artboardWidth,
        height: artboardHeight,
      }),
    };
  }
  if (!found.parentID) {
    return {
      nodes,
      nodeLayouts: {
        ...nodeLayouts,
        [nodeID]: {
          ...(nodeLayouts[nodeID] || {}),
          ...abs,
        },
      },
    };
  }
  const parentBox = getAbsolutePx({
    nodes,
    nodeLayouts,
    nodeID: found.parentID,
    artboardWidth,
    artboardHeight,
  });
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID,
      patch: {
        layout: {
          ...(found.node.layout || {}),
          x: abs.x - (parentBox?.x || 0),
          y: abs.y - (parentBox?.y || 0),
          width: abs.width,
          height: abs.height,
        },
      },
    }),
  };
};

const nextHighestZIndex = (zIndexes = []) =>
  zIndexes.reduce((maxZ, z) => Math.max(maxZ, Number(z) || 0), 0) + 1;

const nextRootInsertZIndex = (nodeLayouts = {}) =>
  nextHighestZIndex(Object.values(nodeLayouts).map((layout) => layout?.zIndex));

const nextGroupInsertZIndex = (children = {}) =>
  nextHighestZIndex(
    Object.values(children).map((child) => child?.layout?.zIndex)
  );

export const insertNodeAtOverlay = ({
  nodes,
  nodeLayouts,
  parentID,
  node,
  nx,
  ny,
  nw,
  nh,
  artboardWidth,
  artboardHeight,
}) => {
  if (countNodes({ nodes }) >= CREATIVE_TEMPLATE_MAX_NODES) {
    return { nodes, nodeLayouts, ok: false };
  }
  const abs = pxBox({
    nx,
    ny,
    nw,
    nh,
    width: artboardWidth,
    height: artboardHeight,
  });
  const parent = parentID
    ? nodes[parentID] ||
      findNodePath({ nodes, nodeID: parentID })?.node
    : null;
  if (parent?.type === "group") {
    const parentBox = getAbsolutePx({
      nodes,
      nodeLayouts,
      nodeID: parent.id,
      artboardWidth,
      artboardHeight,
    });
    const child = {
      ...node,
      layout: {
        x: abs.x - (parentBox?.x || 0),
        y: abs.y - (parentBox?.y || 0),
        width: abs.width,
        height: abs.height,
        zIndex: nextGroupInsertZIndex(parent.children),
        visible: true,
      },
    };
    const children = { ...(parent.children || {}), [node.id]: child };
    return {
      ok: true,
      nodeLayouts,
      nodes: patchNodeById({
        nodes,
        nodeID: parent.id,
        patch: {
          children,
          childOrder: [
            ...normalizeChildOrder({
              childOrder: parent.childOrder,
              children: parent.children,
            }),
            node.id,
          ],
        },
      }),
    };
  }
  return {
    ok: true,
    nodes: { ...nodes, [node.id]: node },
    nodeLayouts: {
      ...nodeLayouts,
      [node.id]: {
        ...abs,
        zIndex: nextRootInsertZIndex(nodeLayouts),
        visible: true,
        rotation: 0,
      },
    },
  };
};

const cloneNode = ({ node, createID, idMap }) => {
  const newID = createID();
  idMap[node.id] = newID;
  const children = {};
  const childOrder = normalizeChildOrder({
    childOrder: node.childOrder,
    children: node.children,
  }).map((childID) => {
    const cloned = cloneNode({
      node: node.children[childID],
      createID,
      idMap,
    });
    children[cloned.id] = cloned;
    return cloned.id;
  });
  return {
    ...node,
    id: newID,
    children: node.children ? children : undefined,
    childOrder: node.children ? childOrder : undefined,
  };
};

export const duplicateNodeById = ({
  nodes,
  nodeLayouts,
  nodeID,
  createID,
}) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  const cloned = cloneNode({ node: found.node, createID, idMap: {} });
  cloned.name = uniqueName({
    name: found.node.name,
    existingNames: listNodeNames({ nodes }),
  });
  if (!found.parentID) {
    const layout = nodeLayouts[nodeID] || {};
    return {
      nodes: { ...nodes, [cloned.id]: cloned },
      nodeLayouts: {
        ...nodeLayouts,
        [cloned.id]: {
          ...layout,
          x: (layout.x || 0) + 16,
          y: (layout.y || 0) + 16,
        },
      },
    };
  }
  const parent = findNodePath({ nodes, nodeID: found.parentID })?.node;
  const children = { ...(parent.children || {}), [cloned.id]: cloned };
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID: parent.id,
      patch: {
        children,
        childOrder: [
          ...normalizeChildOrder({
            childOrder: parent.childOrder,
            children: parent.children,
          }),
          cloned.id,
        ],
      },
    }),
  };
};

export const setNodeVisible = ({ nodes, nodeLayouts, nodeID, visible }) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return { nodes, nodeLayouts };
  if (!found.parentID) {
    return {
      nodes,
      nodeLayouts: {
        ...nodeLayouts,
        [nodeID]: { ...(nodeLayouts[nodeID] || {}), visible },
      },
    };
  }
  return {
    nodeLayouts,
    nodes: patchNodeById({
      nodes,
      nodeID,
      patch: {
        layout: { ...(found.node.layout || {}), visible },
      },
    }),
  };
};

export const isNodeVisible = ({ nodes, nodeLayouts, nodeID }) => {
  const found = findNodePath({ nodes, nodeID });
  if (!found) return true;
  if (!found.parentID) return nodeLayouts[nodeID]?.visible !== false;
  return found.node.layout?.visible !== false;
};

export const syncVariablesFromNodes = ({ nodes = {}, variables = {} }) => {
  const next = {};
  const walk = (map) => {
    Object.values(map).forEach((node) => {
      if (node.source?.kind === "data" && node.source.variable) {
        const key = node.source.variable;
        next[key] = {
          ...(variables[key] || {}),
          label: node.name,
          type: node.type === "image" ? "image" : "string",
          required: false,
          editable: true,
        };
      }
      if (node.children) walk(node.children);
    });
  };
  walk(nodes);
  return next;
};

export const syncVariablesFromArtboards = ({
  artboards = {},
  nodes = {},
  variables = {},
}) => {
  const next = {};
  const ingest = (tree) => {
    Object.assign(
      next,
      syncVariablesFromNodes({
        nodes: tree || {},
        variables: { ...variables, ...next },
      })
    );
  };
  Object.values(artboards || {}).forEach((artboard) => ingest(artboard?.nodes));
  ingest(nodes);
  return next;
};

const sanitizeNode = ({ node, id, isRoot }) => {
  if (!node) return null;
  const clean = { ...node, id: node.id || id };
  if (isRoot) delete clean.layout;
  if (clean.type === "group" || clean.type === "shape") delete clean.source;
  if (
    clean.source?.kind === "data" &&
    !String(clean.source.variable || "").trim()
  ) {
    clean.source = {
      ...clean.source,
      variable: slugifyName({ name: clean.name, fallback: clean.id }),
    };
  }
  if (clean.source?.kind === "generated") {
    const nextSource = { ...clean.source };
    delete nextSource.negativePrompt;
    clean.source = {
      ...nextSource,
      generator: clean.type === "text" ? "text" : "image",
    };
  }
  if (clean.type !== "group") {
    delete clean.children;
    delete clean.childOrder;
    delete clean.componentFrame;
    delete clean.clipContent;
  } else if (clean.children) {
    clean.children = sanitizeNodesForPersist({
      nodes: clean.children,
      isRoot: false,
    });
  }
  if (clean.type !== "text") {
    delete clean.style;
    delete clean.overflow;
  } else {
    clean.overflow = {
      ...(clean.overflow || {}),
      strategy: "shrink",
    };
  }
  if (clean.type === "image") {
    const keepBackground = isCanvasBackgroundNode({
      node: clean,
      parentID: isRoot ? null : clean.id,
    });
    if (keepBackground) {
      clean.isBackground = true;
      clean.fit = "cover";
    } else {
      delete clean.isBackground;
      clean.fit = "contain";
    }
    if (clean.constraints) {
      const nextConstraints = { ...clean.constraints };
      delete nextConstraints.minimumWidth;
      delete nextConstraints.safeArea;
      clean.constraints = Object.keys(nextConstraints).length
        ? nextConstraints
        : undefined;
    }
  } else {
    delete clean.fit;
    delete clean.isBackground;
  }
  if (clean.type !== "shape") delete clean.shape;
  return omitUndefinedDeep(clean);
};

export const sanitizeNodesForPersist = ({ nodes = {}, isRoot = true }) => {
  const next = {};
  Object.entries(nodes).forEach(([id, node]) => {
    const clean = sanitizeNode({ node, id, isRoot });
    if (clean) next[id] = clean;
  });
  return next;
};

export const sanitizeGraphForPersist = ({ nodes = {}, nodeLayouts = {} }) => {
  const cleanNodes = sanitizeNodesForPersist({ nodes, isRoot: true });
  const cleanLayouts = {};
  Object.entries(nodeLayouts || {}).forEach(([id, layout]) => {
    if (cleanNodes[id]) cleanLayouts[id] = layout;
  });
  return { nodes: cleanNodes, nodeLayouts: cleanLayouts };
};

export const sanitizeArtboardsForPersist = ({ artboards = {} }) => {
  const nextArtboards = {};
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    const cleanNodes = sanitizeNodesForPersist({
      nodes: artboard?.nodes || {},
      isRoot: true,
    });
    const cleanLayouts = {};
    Object.entries(artboard?.nodeLayouts || {}).forEach(([layoutID, layout]) => {
      if (cleanNodes[layoutID]) cleanLayouts[layoutID] = layout;
    });
    nextArtboards[id] = {
      ...artboard,
      nodes: cleanNodes,
      nodeLayouts: cleanLayouts,
    };
  });
  return { artboards: nextArtboards };
};
